import { useState } from "react";
import { ClipboardPen, Loader2, Send, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { ROTULO_DO_STATUS_DA_ANAMNESE, type StatusDaAnamnese } from "@bookdental/schemas";
import { Button } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { useConfirm } from "@/lib/confirm";
import { usePermissao } from "@/lib/use-permissao";
import { useConfiguracaoDePacientes } from "@/lib/use-configuracao-de-pacientes";
import { EditorNaClinica } from "./EditorNaClinica";
import { EnviarAnamneseDialog } from "./EnviarAnamneseDialog";
import { LeituraDaAnamnese } from "./LeituraDaAnamnese";
import { SelosDeAlerta } from "./SelosDeAlerta";

type Paciente = { id: string; name: string; phone: string | null; email: string | null; birthDate: string | Date | null };

const COR: Record<StatusDaAnamnese, string> = {
  RASCUNHO: "bg-zinc-100 text-zinc-700",
  ENVIADA: "bg-sky-50 text-sky-700 ring-1 ring-sky-200",
  RESPONDIDA: "bg-amber-50 text-amber-800 ring-1 ring-amber-200",
  REVISADA: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
  CANCELADA: "bg-zinc-100 text-zinc-500",
};
const dia = (d: Date | string | null) => (d ? new Date(d).toLocaleDateString("pt-BR") : "—");

/**
 * Aba Anamnese da ficha. Quem tem "ver pacientes" vê o histórico (estado e datas) e, com
 * "gerenciar pacientes", envia o link; as respostas e os alertas pedem "Ver anamneses".
 */
export function AbaAnamnese({ paciente }: { paciente: Paciente }) {
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  const podeVer = usePermissao("anamnese.view");
  const podePreencher = usePermissao("anamnese.manage");
  const podeEnviar = usePermissao("patients.manage");
  const lista = trpc.anamnese.listar.useQuery({ patientId: paciente.id });
  // Renovação de Configurações › Pacientes: passado o prazo, a última respondida conta como vencida.
  const { renovarACadaMeses } = useConfiguracaoDePacientes().anamnese;
  const situacao = trpc.anamnese.situacao.useQuery({ patientIds: [paciente.id] }, { enabled: renovarACadaMeses > 0 });
  const vencida = renovarACadaMeses > 0 && situacao.data?.porPaciente[paciente.id] === "vencida";
  const [aberta, setAberta] = useState<{ id: string; modo: "editar" | "ler" } | null>(null);
  const [enviando, setEnviando] = useState(false);
  const invalidar = () => utils.anamnese.listar.invalidate({ patientId: paciente.id });
  const abrirNaClinica = trpc.anamnese.abrirNaClinica.useMutation({
    onSuccess: (r) => (invalidar(), setAberta({ id: r.id, modo: "editar" })),
    onError: (e) => toast.error(e.message),
  });
  const revogar = trpc.anamnese.revogarLink.useMutation({ onSuccess: () => (invalidar(), toast.success("Link revogado.")), onError: (e) => toast.error(e.message) });

  if (aberta?.modo === "editar") return <EditorNaClinica anamneseId={aberta.id} onVoltar={() => setAberta(null)} />;
  if (aberta?.modo === "ler") return <LeituraDaAnamnese anamneseId={aberta.id} onVoltar={() => setAberta(null)} />;

  const itens = lista.data ?? [];
  const aguardando = itens.find((a) => a.status === "ENVIADA");
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Anamnese</h3>
          <p className="mt-0.5 text-xs text-zinc-500">Ficha de saúde do paciente: o paciente responde pelo link ou a equipe preenche na clínica.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {podeEnviar && (
            <Button variant="outline" onClick={() => setEnviando(true)}>
              <Send className="mr-1.5 h-4 w-4" /> {aguardando ? "Gerar novo link" : "Enviar ao paciente"}
            </Button>
          )}
          {podePreencher && !aguardando && (
            <Button onClick={() => abrirNaClinica.mutate({ patientId: paciente.id })} disabled={abrirNaClinica.isPending}>
              {abrirNaClinica.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ClipboardPen className="mr-1.5 h-4 w-4" />}
              Preencher na clínica
            </Button>
          )}
        </div>
      </div>

      {vencida && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          Anamnese vencida: a última foi respondida há mais de {renovarACadaMeses} meses. {podeEnviar ? "Envie uma nova ao paciente — ela já vem preenchida com as respostas anteriores." : "Peça a quem envia anamneses para mandar uma nova."}
        </p>
      )}

      {lista.isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
        </div>
      ) : itens.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-200 p-8 text-center dark:border-border">
          <Stethoscope className="mx-auto h-9 w-9 text-zinc-300" />
          <p className="mt-2 text-sm font-medium text-zinc-700 dark:text-foreground">Nenhuma anamnese ainda</p>
          <p className="mt-1 text-xs text-zinc-500">Envie o link para o paciente responder antes da consulta.</p>
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100 rounded-xl border border-zinc-200 dark:divide-border dark:border-border">
          {itens.map((a) => {
            const lerOuEditar = a.status === "RASCUNHO" ? (podePreencher ? "editar" : null) : a.status === "RESPONDIDA" || a.status === "REVISADA" ? (podeVer ? "ler" : null) : null;
            return (
              <li key={a.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", COR[a.status])}>{ROTULO_DO_STATUS_DA_ANAMNESE[a.status]}</span>
                    <span className="text-xs text-zinc-500">
                      {a.status === "REVISADA"
                        ? `Revisada em ${dia(a.revisadaEm)} por ${a.revisadaPorNome}`
                        : a.respondidaEm
                          ? `Respondida em ${dia(a.respondidaEm)}`
                          : a.status === "ENVIADA" && a.link
                            ? a.link.bloqueado
                              ? "Link bloqueado por tentativas erradas — gere outro"
                              : `Link enviado em ${dia(a.enviadaEm)} · vale até ${dia(a.link.expiraEm)}`
                            : `Começada em ${dia(a.criadoEm)} · ${a.progresso}% respondido`}
                    </span>
                  </div>
                  <SelosDeAlerta alertas={a.alertas} compacto />
                </div>
                {a.status === "ENVIADA" && podeEnviar && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      if (await confirm({ title: "Revogar o link?", description: "O paciente não consegue mais abrir. A anamnese volta para preenchimento na clínica.", confirmText: "Revogar", cancelText: "Voltar" }))
                        revogar.mutate({ id: a.id });
                    }}
                  >
                    Revogar link
                  </Button>
                )}
                {lerOuEditar && (
                  <Button variant="outline" size="sm" onClick={() => setAberta({ id: a.id, modo: lerOuEditar })}>
                    {lerOuEditar === "editar" ? "Continuar" : "Abrir"}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {!podeVer && itens.length > 0 && (
        <p className="text-xs text-zinc-500">As respostas são dado de saúde: só quem tem a permissão "Ver anamneses" consegue abrir.</p>
      )}
      <EnviarAnamneseDialog aberto={enviando} onFechar={() => setEnviando(false)} paciente={paciente} />
    </div>
  );
}
